import { FileExcelOutlined, FileSearchOutlined } from '@ant-design/icons'
import { Button, Row, Col, message, Switch, Table, Tabs } from 'antd'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import queryString from 'query-string'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAsync } from '../../../hooks'
import useExportExcel from '../../../hooks/useExportExcel'
import { formatDOB } from '../../../lib/datetime'
import {
  markBillingOrderAsBilled,
  useBillingOrders,
  useBillingOrdersPagination,
  useFetchBillingOrders,
  useFetchCPTCodes,
  useCPTCodes,
} from '../../../redux/slices/billing-orders'
import DownloadLink from '../../Common/DownloadLink'
import { toPath } from '../../../routes'
import { ListError } from '../../Common'

const { TabPane } = Tabs

const COLUMNS = [
  {
    title: 'Last Name',
    dataIndex: ['patient', 'lastName'],
    key: 'lastName',
  },
  {
    title: 'First Name',
    dataIndex: ['patient', 'firstName'],
    key: 'firstName',
  },
  {
    title: 'MRN',
    dataIndex: 'patient.practiceMrn',
    key: 'practiceMrn',
  },
  {
    title: 'DOB',
    key: 'dob',
    dataIndex: ['patient', 'birthDate'],
    render: formatDOB,
  },
  {
    title: 'Device Type',
    dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Tech DOS',
    key: 'billingDate',
    render(text, billingOrder) {
      if (!billingOrder.billingDate) return '-'
      return formatDOB(billingOrder.billingDate)
    },
  },
  {
    title: 'Pro DOS',
    key: 'reportSignatureDate',
    render(text, billingOrder) {
      if (!billingOrder.reportSignatureDate) return '-'
      return formatDOB(billingOrder.reportSignatureDate)
    },
  },
  {
    title: 'Auth #',
    key: 'auth',
    render(_text, billingOrder) {
      if (!billingOrder.preAuthorization) return null

      const { insuranceAuthorizationFilePath, insuranceAuthorizationNumber } =
        billingOrder.preAuthorization
      if (!insuranceAuthorizationFilePath) return insuranceAuthorizationNumber

      return (
        <DownloadLink path={insuranceAuthorizationFilePath}>
          <span>{insuranceAuthorizationNumber}</span>
        </DownloadLink>
      )
    },
  },
  {
    title: 'Actions',
    key: 'actions',
    render(text, billingOrder) {
      return (
        <Link
          to={toPath.billing.remoteBillingOrders.remoteBillingOrder({
            id: billingOrder.id,
          })}
        >
          <Button type="link" icon={<FileSearchOutlined />}>
            Comments / More
          </Button>
        </Link>
      )
    },
  },
]

const CPT_CODE_COLUMNS = [
  {
    title: 'CPT Codes',
    dataIndex: 'cptCode',
    key: 'cptCode',
    width: '80%',
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
  },
]

export default function BillingOrders({
  cycleId,
  onPageChange,
  page = 1,
  query = '',
  billed = 'all',
  practiceId,
}) {
  const dispatch = useDispatch()
  const billingOrders = useBillingOrders(cycleId)
  const cptCodes = useCPTCodes()
  const { pageSize, total } = useBillingOrdersPagination(cycleId)
  const { pending, error, clearError } = useAsync(
    useFetchBillingOrders(cycleId, page, query, billed),
  )
  const {
    pending: cptPending,
    error: cptError,
    clearError: clearCPTError,
  } = useAsync(useFetchCPTCodes(practiceId, cycleId))

  const handleBilledChange = useCallback(
    async (id, isBilled) => {
      try {
        await dispatch(markBillingOrderAsBilled(id, isBilled))
        message.success('Billing Order has been updated')
      } catch (e) {
        message.error(
          get(
            e,
            'response.errors.billed',
            'Oops, we had a problem. Please try submitting again',
          ),
        )
      }
    },
    [dispatch],
  )

  const columns = useMemo(() => {
    const copy = [...COLUMNS]
    copy.splice(copy.length - 1, 0, {
      title: 'Billed',
      key: 'billed',
      render: (text, record) => (
        <Switch
          checked={record.billed}
          checkedChildren="Billed"
          unCheckedChildren="Not&nbsp;Billed"
          onChange={b => handleBilledChange(record.id, b)}
        />
      ),
    })
    return copy
  }, [handleBilledChange])

  const exportUrl = useMemo(
    () =>
      queryString.stringifyUrl(
        {
          url: `/cycles/${cycleId}/billing-orders.xlsx`,
          query: {
            query,
            billed,
          },
        },
        { skipNull: true, skipEmptyString: true },
      ),
    [cycleId, query, billed],
  )

  const [exportingExcel, exportExcel] = useExportExcel(exportUrl)

  const currentCPTCodes = useMemo(() => {
    const current = cptCodes.find(
      entry => entry.practiceId === practiceId && entry.cycleId === cycleId,
    )

    if (current && current.data) {
      return current.data
    }
    return []
  }, [cptCodes, practiceId, cycleId])

  return (
    <Tabs defaultActiveKey="1" style={{ marginBottom: 16 }}>
      <TabPane tab="Billing Orders" key="1">
        {error && (
          <ListError description={error.message} onClose={clearError} />
        )}
        <Table
          size="middle"
          columns={columns}
          dataSource={billingOrders}
          rowKey="id"
          loading={pending}
          pagination={{
            current: page,
            pageSize,
            onChange: onPageChange,
            total,
          }}
        />
        <div>
          <Button
            loading={exportingExcel}
            onClick={exportExcel}
            icon={<FileExcelOutlined />}
            disabled={billingOrders.length === 0}
          >
            Export this list to Excel
          </Button>
        </div>
      </TabPane>
      <TabPane tab="CPT Codes Summary" key="2">
        {cptError && (
          <ListError description={cptError.message} onClose={clearCPTError} />
        )}
        <Row>
          <Col span={12}>
            <Table
              size="middle"
              columns={CPT_CODE_COLUMNS}
              dataSource={currentCPTCodes}
              rowKey="id"
              loading={cptPending}
            />
          </Col>
        </Row>
      </TabPane>
    </Tabs>
  )
}

BillingOrders.propTypes = {
  cycleId: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number,
  query: PropTypes.string,
  billed: PropTypes.string,
  practiceId: PropTypes.string.isRequired,
}
